// Turning a finding into a draft review comment.
//
// The comment sheet is the reviewer's actual deliverable, so this is where the
// product stops describing a problem and starts writing the thing they owe the
// contractor. Two rules make that safe to automate:
//
//   1. A placeholder token can never reach the user. If any {token} in a
//      template cannot be filled from the finding, the whole template is
//      abandoned for the failure mode's genericComment, which carries no
//      tokens at all.
//
//   2. A figure can never reach the user unless the finding already contains
//      it. Two of the templates — FM-15 and FM-08 — are the worked examples
//      COPY.md specifies verbatim, and they name figures from one particular
//      document. Emitted against a different plan they would be a fabrication.
//      Every number in a rendered draft is therefore checked against the
//      finding's own evidence — its receipt, its quote, its location — and a
//      draft asserting a figure that is not there degrades to the generic form.
//
// Neither rule needs the reviewer to notice it. What they see is either a
// comment about their document or a general one, never a wrong one.
//
// The register is fixed by COPY.md: an observation followed by a question.
// Never proposed contract language, never an assertion of who is right, never
// a severity.

import { getFailureMode } from '../../data/taxonomy';
import type { FailureModeId, Flag, Receipt } from '../../types';

/** Everything a draft may be built from. Nothing else is in scope. */
export interface CommentSource {
  failureModeId: FailureModeId;
  sectionRef: string;
  page: number | null;
  quote: string | null;
  receipt?: Receipt;
}

export type DegradeReason =
  /** A {token} in the template had nothing to fill it. */
  | 'missing-input'
  /** The rendered text asserted a figure the finding does not contain. */
  | 'unsupported-figure';

export interface CommentDraft {
  text: string;
  /** True when the generic fallback was used instead of the template. */
  degraded: boolean;
  reason: DegradeReason | null;
  /** The token or figure that forced the fallback. For tests and diagnosis. */
  detail?: string;
}

const TOKEN = /\{([a-zA-Z][a-zA-Z0-9]*)\}/g;

/**
 * Receipt inputs are labelled in prose, and the label a check writes is not
 * something a template should have to spell exactly. Each token names the
 * input it wants through one or more label fragments, matched against the
 * slugified label.
 *
 * Only the four failure modes with a deterministic check have receipts, so
 * only those can carry receipt-filled tokens. The rest reach the reviewer
 * through the model layer, which produces a quote and a location but no
 * receipt, and their templates use {location} alone.
 */
const RECEIPT_TOKENS: Partial<
  Record<FailureModeId, Record<string, string[]>>
> = {
  'FM-02': {
    generation: ['governing generation'],
    obligation: ['witnessing obligation'],
  },
  'FM-04': {
    citation: ['citation as printed', 'citation'],
  },
};

function slug(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function fromReceipt(
  receipt: Receipt | undefined,
  aliases: string[],
): string | null {
  if (!receipt) return null;
  const labelled = receipt.inputs.map((i) => ({ slug: slug(i.label), value: i.value }));
  for (const alias of aliases) {
    const want = slug(alias);
    const hit =
      labelled.find((i) => i.slug === want) ??
      labelled.find((i) => i.slug.startsWith(want)) ??
      labelled.find((i) => i.slug.includes(want));
    if (hit && hit.value.trim()) return hit.value.trim();
  }
  return null;
}

/**
 * "Document" is what sectionRefOf() returns when it could not place a finding
 * in a numbered section. It is a true statement and a useless one to put in a
 * comment — "The reference cited at Document" — so it counts as no location
 * and the draft degrades to the form that does not claim one.
 */
function locationOf(source: CommentSource): string | null {
  const ref = source.sectionRef?.trim();
  if (!ref || ref === 'Document') return null;
  return ref;
}

function resolveToken(source: CommentSource, token: string): string | null {
  if (token === 'location') return locationOf(source);
  if (token === 'page') return source.page === null ? null : String(source.page);

  const aliases = RECEIPT_TOKENS[source.failureModeId]?.[token];
  if (!aliases) return null;
  return fromReceipt(source.receipt, aliases);
}

/* ==========================================================================
   The figure guard
   ========================================================================== */

/** Numbers as they are written in a comment: 899,252 · 0.0525 · 1-1 · 4.2.1.2 */
const FIGURE = /\d(?:[\d.,\-]*\d)?/g;

function normaliseFigures(text: string): string {
  return text.replace(/,/g, '');
}

/**
 * Everything the finding itself says. A draft may assert a figure only if it
 * appears here — in the receipt the reviewer can check on paper, in the quoted
 * clause, or in the location being cited.
 */
function evidenceSurface(source: CommentSource): string {
  const parts: string[] = [source.sectionRef ?? '', source.quote ?? ''];
  if (source.page !== null) parts.push(String(source.page));
  const receipt = source.receipt;
  if (receipt) {
    for (const input of receipt.inputs) parts.push(input.label, input.value);
    if (receipt.computation) parts.push(receipt.computation);
    parts.push(receipt.result);
  }
  return normaliseFigures(parts.join(' \n '));
}

/** The first figure in `text` that the finding does not support, if any. */
function unsupportedFigure(text: string, source: CommentSource): string | null {
  const surface = evidenceSurface(source);
  for (const match of text.matchAll(FIGURE)) {
    const figure = match[0].replace(/[.,\-]+$/, '');
    if (!figure) continue;
    if (!surface.includes(normaliseFigures(figure))) return match[0];
  }
  return null;
}

/* ==========================================================================
   Building the draft
   ========================================================================== */

export function buildCommentDraft(source: CommentSource): CommentDraft {
  const mode = getFailureMode(source.failureModeId);
  const generic = mode.genericComment;

  const missing: string[] = [];
  const rendered = mode.comment_template.replace(TOKEN, (whole, token: string) => {
    const value = resolveToken(source, token);
    if (value === null) {
      missing.push(token);
      return whole;
    }
    return value;
  });

  if (missing.length > 0) {
    return {
      text: generic,
      degraded: true,
      reason: 'missing-input',
      detail: missing[0],
    };
  }

  const unsupported = unsupportedFigure(rendered, source);
  if (unsupported !== null) {
    return {
      text: generic,
      degraded: true,
      reason: 'unsupported-figure',
      detail: unsupported,
    };
  }

  return { text: rendered, degraded: false, reason: null };
}

/** The draft carried on a finding. */
export function commentDraftFor(source: CommentSource): string {
  return buildCommentDraft(source).text;
}

/** Convenience for callers that already hold a whole finding. */
export function commentSourceOf(flag: Flag): CommentSource {
  return {
    failureModeId: flag.failureModeId,
    sectionRef: flag.sectionRef,
    page: flag.page,
    quote: flag.quote,
    receipt: flag.receipt,
  };
}
